/*
 * CreativeAssetPreviewTile — the one creative preview renderer.
 *
 * Creative artwork is not a plain <img src>. A creative asset lives behind an
 * authenticated, advertiser-authorized stream endpoint, its poster frame may
 * have to be captured from the video itself, and several of the paths that
 * reach it can fail in ways that must degrade to a legible placeholder rather
 * than a broken image. All of that behavior lives here so that every surface
 * showing a creative — Creative Trafficking's inventory and the Creative
 * Performance section on individual platform Analytics pages — resolves the
 * same artwork the same way.
 *
 * This module was extracted verbatim from CreativeTrafficking, which remains a
 * consumer. A second copy of this logic is how two surfaces would start
 * disagreeing about whether a creative has artwork.
 */

import React, { useEffect, useRef, useState } from "react";

import { API_BASE_URL, inferResolvedPreviewKind } from "../utils/creativeAssetPreview";

/*
 * Both apps read the same union of keys. Cortex and Quantum are served from
 * different origins, so a browser only ever holds one of `clientAuthToken` and
 * `superadminToken` — which makes the order immaterial and lets this module
 * stay byte-identical across the two repositories.
 */
function getAuthToken() {
  return (
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("superadminToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

async function apiBlob(path, options = {}) {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    signal: options.signal,
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Request failed (${response.status})`);
  }
  return response.blob();
}

export function toRelativeApiPath(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/api/")) return raw;
  if (API_BASE_URL && raw.startsWith(API_BASE_URL)) {
    const next = raw.slice(API_BASE_URL.length);
    return next.startsWith("/") ? next : `/${next}`;
  }
  return "";
}

async function createVideoThumbnailFromBlob(blob) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Document unavailable"));
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.style.position = "fixed";
    video.style.left = "-9999px";
    video.style.top = "0";
    video.style.width = "1px";
    video.style.height = "1px";
    video.style.opacity = "0";
    video.style.pointerEvents = "none";
    document.body.appendChild(video);
    video.src = objectUrl;

    let settled = false;
    let captureTimeoutId = null;
    let seekTimeoutId = null;

    const cleanup = () => {
      if (captureTimeoutId) window.clearTimeout(captureTimeoutId);
      if (seekTimeoutId) window.clearTimeout(seekTimeoutId);
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute("src");
      try {
        video.load();
      } catch (_error) {
        // no-op
      }
      video.remove();
    };

    const captureFrame = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 320;
      canvas.height = video.videoHeight || 180;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) throw new Error("Canvas unavailable");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      return { canvas, context };
    };

    const handleFailure = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Video thumbnail generation failed"));
    };

    const isUsefulFrame = (context, canvas) => {
      try {
        const sampleWidth = Math.min(canvas.width, 48);
        const sampleHeight = Math.min(canvas.height, 27);
        if (sampleWidth <= 0 || sampleHeight <= 0) return false;

        const imageData = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
        let totalBrightness = 0;
        let brightPixels = 0;
        let minBrightness = 255;
        let maxBrightness = 0;
        const pixels = imageData.length / 4;

        for (let index = 0; index < imageData.length; index += 4) {
          const r = imageData[index];
          const g = imageData[index + 1];
          const b = imageData[index + 2];
          const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          totalBrightness += brightness;
          if (brightness > 48) brightPixels += 1;
          if (brightness < minBrightness) minBrightness = brightness;
          if (brightness > maxBrightness) maxBrightness = brightness;
        }

        const averageBrightness = totalBrightness / Math.max(pixels, 1);
        const brightRatio = brightPixels / Math.max(pixels, 1);
        const brightnessRange = maxBrightness - minBrightness;
        return (
          (averageBrightness > 28 && brightRatio > 0.08) ||
          (averageBrightness > 16 && brightnessRange > 42) ||
          (averageBrightness > 12 && brightRatio > 0.025 && brightnessRange > 28)
        );
      } catch (_error) {
        return false;
      }
    };

    const finalizeWithFrame = (dataUrl) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(dataUrl);
    };

    const tryCaptureAtTime = (timeSeconds) =>
      new Promise((resolveAttempt) => {
        if (settled) {
          resolveAttempt(false);
          return;
        }

        const handleAttemptFailure = () => {
          if (seekTimeoutId) window.clearTimeout(seekTimeoutId);
          resolveAttempt(false);
        };

        const evaluateCurrentFrame = () => {
          if (seekTimeoutId) window.clearTimeout(seekTimeoutId);
          try {
            const { canvas, context } = captureFrame();
            if (isUsefulFrame(context, canvas)) {
              finalizeWithFrame(canvas.toDataURL("image/jpeg", 0.84));
              resolveAttempt(true);
              return;
            }
          } catch (_error) {
            // Ignore and continue to the next candidate.
          }
          resolveAttempt(false);
        };

        if (Math.abs(Number(video.currentTime || 0) - Number(timeSeconds || 0)) < 0.04 && video.readyState >= 2) {
          evaluateCurrentFrame();
          return;
        }

        const handleSeeked = () => evaluateCurrentFrame();
        video.addEventListener("seeked", handleSeeked, { once: true });

        seekTimeoutId = window.setTimeout(() => {
          video.removeEventListener("seeked", handleSeeked);
          handleAttemptFailure();
        }, 1600);

        try {
          video.currentTime = Math.max(0, timeSeconds || 0);
        } catch (_error) {
          video.removeEventListener("seeked", handleSeeked);
          handleAttemptFailure();
        }
      });

    const tryCandidateFrames = async () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const candidates = duration > 0
        ? Array.from(
            new Set(
              [0.12, 0.22, 0.34, 0.48, 0.62, 0.78]
                .map((ratio) => Number((duration * ratio).toFixed(3)))
                .filter((time) => time >= 0 && time < duration)
            )
          )
        : [0.15, 0.35, 0.65, 1];

      for (const candidate of candidates) {
        // eslint-disable-next-line no-await-in-loop
        const success = await tryCaptureAtTime(candidate);
        if (success || settled) return;
      }

      handleFailure();
    };

    captureTimeoutId = window.setTimeout(handleFailure, 6000);

    video.addEventListener(
      "loadedmetadata",
      () => {
        if (video.readyState >= 1) {
          void tryCandidateFrames();
        }
      },
      { once: true }
    );

    video.addEventListener(
      "loadeddata",
      () => {
        if (!settled && video.readyState >= 2) {
          void tryCandidateFrames();
        }
      },
      { once: true }
    );

    video.addEventListener(
      "error",
      handleFailure,
      { once: true }
    );
  });
}

function CreativePreviewTile({
  assetName,
  sourceUrl,
  fallbackApiPath = "",
  posterUrl = "",
  mimeType,
  creativeType,
  dimensions,
  className = "",
  fallbackClassName = "",
  controls = false,
  loadingLabel = "Loading Preview",
  thumbnailMode = false,
  thumbnailTimeoutMs = 0,
}) {
  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState("");
  const [resolvedPreviewKind, setResolvedPreviewKind] = useState("image");
  const [resolvedPosterUrl, setResolvedPosterUrl] = useState("");
  const [phase, setPhase] = useState("loading");
  const handleResolvedPreviewError = () => {
    setResolvedPreviewUrl("");
    setResolvedPosterUrl("");
    setPhase("failed");
  };

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    let timeoutId = null;
    let activeAbortController = null;

    const clearPreviewTimeout = () => {
      if (!timeoutId) return;
      window.clearTimeout(timeoutId);
      timeoutId = null;
    };

    const failFast = () => {
      if (cancelled) return;
      setResolvedPreviewUrl("");
      setResolvedPosterUrl("");
      setPhase("failed");
    };

    async function loadPreview() {
      setResolvedPreviewUrl("");
      setResolvedPreviewKind("image");
      setResolvedPosterUrl("");
      setPhase("loading");
      const isVideo = String(mimeType || "").startsWith("video/");
      const effectiveFallbackApiPaths = Array.isArray(fallbackApiPath)
        ? fallbackApiPath.filter(Boolean)
        : fallbackApiPath
        ? [fallbackApiPath]
        : [];
      const candidateApiPaths = [];
      const sourceApiPath = toRelativeApiPath(sourceUrl);
      if (sourceApiPath) candidateApiPaths.push(sourceApiPath);
      effectiveFallbackApiPaths.forEach((path) => {
        if (path && !candidateApiPaths.includes(path)) candidateApiPaths.push(path);
      });
      const fallbackStreamUrl = candidateApiPaths[0]
        ? `${API_BASE_URL}${candidateApiPaths[0]}`
        : "";
      const directPlayableUrl = sourceUrl || fallbackStreamUrl;
      const sourcePreviewKind = inferResolvedPreviewKind(sourceUrl, mimeType, false);
      const directVideoUrl =
        sourcePreviewKind === "video"
          ? sourceUrl
          : fallbackStreamUrl;
      const applyResolvedPreview = (url, kind, nextPosterUrl = "") => {
        if (cancelled || !url) return false;
        setResolvedPreviewUrl(url);
        setResolvedPreviewKind(kind);
        setResolvedPosterUrl(nextPosterUrl);
        setPhase("success");
        clearPreviewTimeout();
        return true;
      };
      const createRenderableBlob = (rawBlob) => {
        const needsMimeOverride =
          rawBlob &&
          String(mimeType || "").trim() &&
          (!rawBlob.type || rawBlob.type === "application/octet-stream");
        return needsMimeOverride ? new Blob([rawBlob], { type: mimeType }) : rawBlob;
      };
      const resolveProtectedMediaFallback = async (apiPaths, previewKind, nextPosterUrl = "") => {
        const normalizedPaths = Array.from(new Set((Array.isArray(apiPaths) ? apiPaths : []).filter(Boolean)));
        for (const apiPath of normalizedPaths) {
          try {
            const rawBlob = await apiBlob(apiPath);
            if (cancelled) return true;
            const blob = createRenderableBlob(rawBlob);
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
              objectUrl = "";
            }
            objectUrl = URL.createObjectURL(blob);
            return applyResolvedPreview(objectUrl, previewKind, nextPosterUrl);
          } catch (_fallbackError) {
            // Try the next protected stream candidate.
          }
        }
        return false;
      };
      const resolveVideoThumbnailFallback = async () => {
        if (!thumbnailMode || !isVideo || !directVideoUrl) return false;
        if (sourceUrl && sourcePreviewKind === "image") return false;
        const protectedVideoPaths = [
          toRelativeApiPath(directVideoUrl),
          ...candidateApiPaths,
        ].filter(Boolean);
        if (protectedVideoPaths.length) {
          const resolvedProtectedVideo = await resolveProtectedMediaFallback(
            protectedVideoPaths,
            "video",
            posterUrl
          );
          if (resolvedProtectedVideo) return true;
        }
        return applyResolvedPreview(directVideoUrl, "video", posterUrl);
      };

      if (!sourceUrl && effectiveFallbackApiPaths.length === 0) {
        timeoutId = window.setTimeout(
          () => {
            failFast();
          },
          thumbnailMode ? Math.max(thumbnailTimeoutMs || 0, 1800) : 0
        );
        return;
      }

      const previewTimeoutMs = thumbnailMode
        ? thumbnailTimeoutMs > 0
          ? thumbnailTimeoutMs
          : 0
        : 15000;

      if (previewTimeoutMs > 0) {
        timeoutId = window.setTimeout(() => {
          if (activeAbortController) activeAbortController.abort();
          void (async () => {
            if (!(await resolveVideoThumbnailFallback())) failFast();
          })();
        }, previewTimeoutMs);
      }

      if (isVideo && !thumbnailMode && directPlayableUrl) {
        applyResolvedPreview(directPlayableUrl, "video", posterUrl);
        return;
      }

      if (candidateApiPaths.length === 0) {
        applyResolvedPreview(
          sourceUrl,
          inferResolvedPreviewKind(sourceUrl, mimeType, thumbnailMode),
          posterUrl
        );
        return;
      }

      for (const apiPath of candidateApiPaths) {
        try {
          activeAbortController = new AbortController();
          const rawBlob = await apiBlob(apiPath, { signal: activeAbortController.signal });
          activeAbortController = null;
          if (cancelled) return;
          const blob = createRenderableBlob(rawBlob);
          if (apiPath === sourceApiPath && sourcePreviewKind === "image") {
            // The source is the governed poster still of a video creative
            // (/api/creative-assets/:id/poster), not the video itself: render
            // the image it is instead of decoding it for a frame.
            objectUrl = URL.createObjectURL(blob);
            applyResolvedPreview(objectUrl, "image", "");
            return;
          }
          if (thumbnailMode && isVideo) {
            try {
              const thumbnailDataUrl = await createVideoThumbnailFromBlob(blob);
              if (cancelled) return;
              setResolvedPreviewUrl(thumbnailDataUrl);
              setResolvedPreviewKind("image");
              setPhase("success");
              clearPreviewTimeout();
              return;
            } catch (_thumbnailError) {
              // Fall back to rendering the video itself as a lightweight thumbnail.
            }
          }
          objectUrl = URL.createObjectURL(blob);
          if (thumbnailMode) {
            applyResolvedPreview(
              objectUrl,
              isVideo ? "video" : inferResolvedPreviewKind(objectUrl, blob.type || mimeType, true),
              posterUrl
            );
            return;
          }
          if (isVideo && !posterUrl) {
            try {
              const posterDataUrl = await createVideoThumbnailFromBlob(blob);
              if (!cancelled) setResolvedPosterUrl(posterDataUrl);
            } catch (_posterError) {
              // Video can still render without a poster.
            }
          } else {
            setResolvedPosterUrl(posterUrl);
          }
          applyResolvedPreview(
            objectUrl,
            inferResolvedPreviewKind(objectUrl, blob.type || mimeType, false),
            posterUrl
          );
          return;
        } catch (_error) {
          activeAbortController = null;
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = "";
          }
          if (cancelled) return;
          // A governed poster is the ONLY still this tile may show for a video
          // creative. When it cannot be served (refused, missing, unverified)
          // the tile settles on its neutral placeholder: it never falls
          // through to a version or source stream to cut a frame, because a
          // frame from anything but the verified poster is not this creative,
          // and a source master is never streamed for a thumbnail.
          if (thumbnailMode && isVideo && apiPath === sourceApiPath && sourcePreviewKind === "image") {
            clearPreviewTimeout();
            failFast();
            return;
          }
        }
      }

      try {
        if (await resolveVideoThumbnailFallback()) {
          return;
        }
        if (sourceUrl) {
          const sourceApiPath = toRelativeApiPath(sourceUrl);
          if (sourceApiPath) {
            const resolvedProtectedSource = await resolveProtectedMediaFallback(
              [sourceApiPath],
              inferResolvedPreviewKind(sourceUrl, mimeType, thumbnailMode),
              posterUrl
            );
            if (resolvedProtectedSource) {
              return;
            }
          }
          applyResolvedPreview(
            sourceUrl,
            inferResolvedPreviewKind(sourceUrl, mimeType, thumbnailMode),
            posterUrl
          );
          return;
        }
        if (thumbnailMode && isVideo) {
          try {
            const fallbackKind = inferResolvedPreviewKind(sourceUrl, mimeType, thumbnailMode);
            if (fallbackKind === "video" && sourceUrl) {
              applyResolvedPreview(sourceUrl, "video", posterUrl);
              return;
            }
          } catch (_thumbnailError) {
            // Ignore and fall through to final failure state.
          }
        }
        clearPreviewTimeout();
        failFast();
      } catch (_error) {
        clearPreviewTimeout();
        failFast();
      }
    }

    loadPreview();

    return () => {
      cancelled = true;
      if (activeAbortController) activeAbortController.abort();
      clearPreviewTimeout();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fallbackApiPath, mimeType, posterUrl, sourceUrl, thumbnailMode, thumbnailTimeoutMs]);

  const fallbackPrimary = "No Preview";
  const fallbackSecondary = thumbnailMode
    ? ""
    : dimensions && dimensions !== "Flexible"
    ? dimensions
    : creativeType || "Asset";

  if (phase === "loading") {
    return (
      <div className={`creativetrafficking-preview-loading ${fallbackClassName}`.trim()}>
        <span className="creativetrafficking-preview-loading-orb" aria-hidden="true" />
        <span className="creativetrafficking-preview-loading-label">{loadingLabel}</span>
      </div>
    );
  }

  if (resolvedPreviewUrl) {
    if (resolvedPreviewKind === "video") {
      return (
        <PreviewVideo
          className={className}
          src={resolvedPreviewUrl}
          poster={resolvedPosterUrl || posterUrl || undefined}
          controls={controls}
          thumbnailMode={thumbnailMode}
          onError={handleResolvedPreviewError}
        />
      );
    }
    return (
      <img
        className={className}
        src={resolvedPreviewUrl}
        alt={assetName}
        onError={handleResolvedPreviewError}
        onLoad={(event) => {
          // Fallback: when no width/height metadata set the frame aspect ratio,
          // refine it from the loaded image's natural dimensions so the preview
          // still matches the creative's true shape. Scoped to row cells.
          const img = event.currentTarget;
          if (!img?.naturalWidth || !img?.naturalHeight) return;
          const cell =
            typeof img.closest === "function"
              ? img.closest(".creativetrafficking-preview-cell")
              : null;
          if (cell && !cell.style.getPropertyValue("--preview-ar")) {
            cell.style.setProperty(
              "--preview-ar",
              String(img.naturalWidth / img.naturalHeight)
            );
          }
        }}
      />
    );
  }

  return (
    <div className={`creativetrafficking-preview-fallback ${fallbackClassName}`.trim()}>
      <span className="creativetrafficking-preview-fallback-title">{fallbackPrimary}</span>
      {phase === "failed" && fallbackSecondary ? (
        <span className="creativetrafficking-preview-fallback-meta">{fallbackSecondary}</span>
      ) : null}
    </div>
  );
}

function PreviewVideo({ className, src, poster, controls, thumbnailMode = false, onError }) {
  const videoRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    setIsVideoReady(false);
  }, [poster, src, thumbnailMode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !thumbnailMode) return undefined;

    let cancelled = false;

    const seekToThumbnailFrame = () => {
      if (cancelled || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const target = Math.min(Math.max(video.duration * 0.22, 0.15), Math.max(video.duration - 0.1, 0.15));
      try {
        video.currentTime = target;
      } catch (_error) {
        // Keep the default first frame if seeking fails.
      }
    };

    const pauseAtFrame = () => {
      if (!cancelled) video.pause();
    };

    video.addEventListener("loadedmetadata", seekToThumbnailFrame);
    video.addEventListener("seeked", pauseAtFrame);
    if (video.readyState >= 1) seekToThumbnailFrame();

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", seekToThumbnailFrame);
      video.removeEventListener("seeked", pauseAtFrame);
    };
  }, [src, thumbnailMode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    if (video.readyState >= 2) {
      setIsVideoReady(true);
      return undefined;
    }

    const handleReady = () => setIsVideoReady(true);
    video.addEventListener("loadeddata", handleReady);
    video.addEventListener("canplay", handleReady);

    return () => {
      video.removeEventListener("loadeddata", handleReady);
      video.removeEventListener("canplay", handleReady);
    };
  }, [src]);

  const handleVideoError = (event) => {
    setIsVideoReady(false);
    if (typeof onError === "function") onError(event);
  };

  return (
    <div className="creativetrafficking-preview-video-shell">
      <video
        ref={videoRef}
        className={className}
        src={src}
        poster={poster || undefined}
        muted
        playsInline
        preload={thumbnailMode ? "metadata" : "auto"}
        controls={controls}
        onLoadedData={() => setIsVideoReady(true)}
        onCanPlay={() => setIsVideoReady(true)}
        onError={handleVideoError}
      />
      {!isVideoReady ? (
        <div className="creativetrafficking-preview-loading creativetrafficking-preview-video-loader">
          <span className="creativetrafficking-preview-loading-orb" aria-hidden="true" />
          <span className="creativetrafficking-preview-loading-label">Loading Preview</span>
        </div>
      ) : null}
    </div>
  );
}

export default CreativePreviewTile;
export { CreativePreviewTile };
